import { Person } from "@/Common/Models/Person/Person";
import { DocumentUIState } from "@/Common/Models/UI/Enums";
import { PersonDropdownItem } from "@/Common/Models/UI/Inputs/PersonDropdownItem";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as SubServerOperations from "@/Common/Operations/operations.server.sub";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { BaseOperationHandler } from "@/Common/Stores/Handlers/Local/BaseOperationHandler";
import { InboundFaxMailboxModel } from "@/Documents/Models/InboundFax/InboundFaxMailboxModel";
import { DocumentsStore } from "@/Documents/Stores/DocumentsStore";

class UpdateDocumentHandler extends BaseOperationHandler {
    constructor() {
        super();

        this.RegisterOperation(LocalOperations.Documents.Show_DocumentSearch, this.ShowSearchView);
        this.RegisterOperation(LocalOperations.Documents.View_ShowEdit, this.ShowEditView);
        this.RegisterOperation(LocalOperations.Documents.Edit_Patient_Selection_Change, this.SelectPatient);
        this.RegisterOperation(LocalOperations.Documents.Edit_Patient_ClearSelection, this.ClearSelection);
        this.RegisterOperation(LocalOperations.Documents.Edit_Clinician_Selection_Change, this.selectClinician);
        this.RegisterOperation(LocalOperations.Documents.Edit_Set_Document_Date, this.OnDocumentDateChange);
        this.RegisterOperation(LocalOperations.Documents.Edit_OnTodayClick, this.OnTodayClick);
        this.RegisterOperation(LocalOperations.Documents.Edit_HRMStatusChange, this.OnHRMStatusChange);
        this.RegisterOperation(LocalOperations.Documents.Edit_OnCancel, this.OnCancelClick);
        this.RegisterOperation(LocalOperations.Documents.Edit_Note_Change, this.OnNoteChange);
        this.RegisterOperation(LocalOperations.Documents.Edit_OnSaveClick, this.SaveDocument);
        this.RegisterOperation(LocalOperations.Documents.Edit_BeginUpload, this.BeginUpload);
        this.RegisterOperation(LocalOperations.Documents.Edit_OnApproveClick, this.ApproveDocument);
        this.RegisterOperation(LocalOperations.Documents.Edit_Document_ShowDetails, this.ToggleDetailsDisplay);
        this.RegisterOperation(LocalOperations.Documents.Edit_SendingFacilityIdChange, this.SenderFacilityIdChange);
        this.RegisterOperation(LocalOperations.Documents.Edit_SendingFacilityReportChange, this.SenderFacilityReportChange);
        this.RegisterOperation(LocalOperations.Documents.Edit_AccompanyingSubClassChange, this.AccompanyingSubClassChange);
        this.RegisterOperation(LocalOperations.Documents.Edit_MnemonicChange, this.MnemonicChange);
        this.RegisterOperation(LocalOperations.Documents.Edit_SourceAuthorNameChange, this.SourceAuthorNameChange);
        this.RegisterOperation(LocalOperations.Documents.Edit_AuthoringDateChange, this.AuthoringDateChange);
        this.RegisterOperation(LocalOperations.Documents.Edit_ObservationDateChange, this.ObservationDateChange);
        this.RegisterOperation(LocalOperations.Documents.Edit_OnSourcesChange, this.SourcesChange);
        this.RegisterOperation(LocalOperations.Documents.Edit_OnDescriptionChange, this.DescriptionChange);
        this.RegisterOperation(LocalOperations.Documents.Edit_SubClassChange, this.SubClassChange);
        this.RegisterOperation(LocalOperations.Documents.Edit_OnUrgencyCheckChange, this.UrgencyChange);
        this.RegisterOperation(LocalOperations.Documents.Edit_UploadComplete, this.UploadComplete);
        this.RegisterOperation(LocalOperations.Documents.Create_UploadFailPatientProfile, this.UploadFailed);
        this.RegisterOperation(LocalOperations.Documents.Edit_SiteChange, this.SiteChange);
        this.RegisterOperation(LocalOperations.Documents.Edit_SaveApprovedDocument, this.SaveApprovedDocument);
        this.RegisterOperation(LocalOperations.Documents.Edit_OnFileSelectionChange, this.onFileSelectionChange);
        this.RegisterOperation(LocalOperations.Documents.Edit_Clinician_remove, this.onRemoveSelectedClinician);
        this.RegisterOperation(LocalOperations.Documents.View_LoadAttachedTasks, this.loadAttachedTasks);
        this.RegisterOperation(LocalOperations.Documents.InboundFaxes_UpdateData, this.UpdateInboundFaxData);
    }

    private ShowSearchView(request: LocalEvent, store: DocumentsStore): void {
        store.DocumentsModel = store.DocumentsModel.set("UIState", DocumentUIState.Search);
    }

    private ShowEditView(request: LocalEvent, store: DocumentsStore): void {
        store.DocumentsModel = store.DocumentsModel.set("UIState", DocumentUIState.Edit);
    }

    private ToggleDetailsDisplay(request: LocalEvent, store: DocumentsStore): void {
        var newDoc = store.DocumentsModel.get("SelectedDocument");
        var t = !newDoc.get("AdditionalDetailsVisible");
        newDoc = newDoc.set("AdditionalDetailsVisible", t);
        if (t) {
            newDoc = newDoc.set("DetailsLinkLabel", cacheStore.GetString("HideDocumentDetailsLabel"));
            newDoc = newDoc.set("DetailsLinkImagePath", rootPath + "../content/images/icons/documents/add_details_open.png");
        }
        else {
            newDoc = newDoc.set("DetailsLinkLabel", cacheStore.GetString("AddDocumentDetailsLabel"));
            newDoc = newDoc.set("DetailsLinkImagePath", rootPath + "../content/images/icons/documents/add_details.png");
        }
        store.DocumentsModel = store.DocumentsModel.set("SelectedDocument", newDoc);
    }

    private BeginUpload(request: LocalEvent, store: DocumentsStore): void {
        // initiate file upload
        store.DocumentsModel = store.DocumentsModel.set("BeginUpload", true);
    }

    private SaveDocument(request: LocalEvent, store: DocumentsStore): void {
        var selectedDocument = store.DocumentsModel
            .get("SelectedDocument")
            .set("IsSaving", true);

        store.DocumentsModel = store.DocumentsModel
            .set("SelectedDocument", selectedDocument);

        // post document to server
        store.DocumentActions.UpdateExistingDocument(selectedDocument);
    }

    private UploadComplete(request: LocalEvent, store: DocumentsStore): void {
        let selectedDocument = store.DocumentsModel
            .get("SelectedDocument")
            .set("IsSaving", false);

        if (selectedDocument && selectedDocument.get("MedicalDocumentInfo"))
            store.DocumentActions.LoadDocument(selectedDocument.get("MedicalDocumentInfo").Model.Id);

        store.DocumentsModel = store.DocumentsModel
            .set("BeginUpload", false)
            .set("UIState", DocumentUIState.View)
            .set("SelectedDocument", selectedDocument);
    }

    private UploadFailed(request: LocalEvent, store: DocumentsStore): void {
        let selectedDocument = store.DocumentsModel
            .get("SelectedDocument")
            .set("IsSaving", false);

        store.DocumentsModel = store.DocumentsModel
            .set("BeginUpload", false)
            .set("SelectedDocument", selectedDocument);
    }

    private onFileSelectionChange(request: LocalEvent, store: DocumentsStore): void {
        const count = request.Get<number>("count");
        const files = request.Get<File[]>("files");

        const selectedDoc = store.DocumentsModel.get("SelectedDocument")
            .set("NumberOfFiles", count)
            .set("Files", files);

        store.DocumentsModel = store.DocumentsModel.set("SelectedDocument", selectedDoc);
    }

    private ApproveDocument(request: LocalEvent, store: DocumentsStore): void {
        var docId = store.DocumentsModel.get("SelectedDocumentId");
        var docIdArray: string[] = [];
        docIdArray.push(docId.toString());
        store.DocumentActions.ApproveDocuments(docIdArray, SubServerOperations.Documents.ApproveFromDocument);
    }

    private UrgencyChange(request: LocalEvent, store: DocumentsStore): void {
        var newDoc = store.DocumentsModel.get("SelectedDocument");
        var mDoc = newDoc.get("MedicalDocumentInfo");
        mDoc.Model.IsUrgent = request.Get<boolean>("value");
        newDoc = newDoc.delete("MedicalDocumentInfo");
        newDoc = newDoc.set("MedicalDocumentInfo", mDoc);
        store.DocumentsModel = store.DocumentsModel.set("SelectedDocument", newDoc);
    }

    private OnCancelClick(request: LocalEvent, store: DocumentsStore): void {
        let docId = store.DocumentsModel.get("SelectedDocumentId");
        store.DocumentActions.LoadDocument(docId.toString());
    }

    private SiteChange(request: LocalEvent, store: DocumentsStore): void {
        var siteId = request.Get<string>("value");
        var newDoc = store.DocumentsModel.get("SelectedDocument");
        var mDoc = newDoc.get("MedicalDocumentInfo");
        mDoc.Model.SiteId = siteId;
        newDoc = newDoc.delete("MedicalDocumentInfo");
        newDoc = newDoc.set("MedicalDocumentInfo", mDoc);
        store.DocumentsModel = store.DocumentsModel.set("SelectedDocument", newDoc);
    }

    private SelectPatient(request: LocalEvent, store: DocumentsStore): void {
        var patientItem = request.Get<PersonDropdownItem>("value");
        var selectedDoc = store.DocumentsModel.get("SelectedDocument");
        var list = selectedDoc.get("SelectedPatients").toArray();
        function indexOf(id: string, collection: Person[]): number {
            var idx = -1;
            for (var i = 0; i < collection.length; i++) {
                if (id === collection[i].ContactId) {
                    idx = i;
                    break;
                }
            } return idx;
        }
        var idx = indexOf(patientItem.Item.ContactId, list);
        if (idx === -1) { list.push(patientItem.Item); }
        else { list.splice(idx, 1); }

        selectedDoc = selectedDoc.set("SelectedPatients", Immutable.List(list));
        store.DocumentsModel = store.DocumentsModel.set("SelectedDocument", selectedDoc);
    }

    private ClearSelection(request: LocalEvent, store: DocumentsStore): void {
        var doc = store.DocumentsModel.get("SelectedDocument");
        doc = doc.set("SelectedPatients", Immutable.List([]));
        store.DocumentsModel = store.DocumentsModel.set("SelectedDocument", doc);
    }

    private selectClinician(request: LocalEvent, store: DocumentsStore): void {
        let selectedClinician = request.Get<PersonDropdownItem>("value");

        if (selectedClinician == null || selectedClinician.Item == null)
            return;

        let newDocument = store.DocumentsModel.get("SelectedDocument");
        let responsibleProviders = newDocument.get("SelectedClinicians").toArray();

        responsibleProviders.map(x => x.ContactId).indexOf(selectedClinician.Item.ContactId) > -1
            ? responsibleProviders = responsibleProviders.filter(x => x.ContactId != selectedClinician.Item.ContactId)
            : responsibleProviders.push(selectedClinician.Item);

        newDocument = newDocument.set("SelectedClinicians", Immutable.List<Person>(responsibleProviders));
        store.DocumentsModel = store.DocumentsModel.set("SelectedDocument", newDocument);
    }

    private SubClassChange(request: LocalEvent, store: DocumentsStore): void {
        var newDoc = store.DocumentsModel.get("SelectedDocument");
        var mDoc = newDoc.get("MedicalDocumentInfo");
        mDoc.Model.ClassId = request.Get<string>("value_class");
        mDoc.Model.SubClassId = request.Get<string>("value_subclass");
        newDoc = newDoc.delete("MedicalDocumentInfo");
        newDoc = newDoc.set("MedicalDocumentInfo", mDoc);
        store.DocumentsModel = store.DocumentsModel.set("SelectedDocument", newDoc);
    }

    private SourcesChange(request: LocalEvent, store: DocumentsStore): void {
        var newDoc = store.DocumentsModel.get("SelectedDocument");
        var mDoc = newDoc.get("MedicalDocumentInfo");
        mDoc.Model.Source = request.Get<string>("value");
        newDoc = newDoc.delete("MedicalDocumentInfo");
        newDoc = newDoc.set("MedicalDocumentInfo", mDoc);
        store.DocumentsModel = store.DocumentsModel.set("SelectedDocument", newDoc);
    }

    private DescriptionChange(request: LocalEvent, store: DocumentsStore): void {
        var newDoc = store.DocumentsModel.get("SelectedDocument");
        var mDoc = newDoc.get("MedicalDocumentInfo");
        mDoc.Model.Subject = request.Get<string>("value");
        newDoc = newDoc.delete("MedicalDocumentInfo");
        newDoc = newDoc.set("MedicalDocumentInfo", mDoc);
        store.DocumentsModel = store.DocumentsModel.set("SelectedDocument", newDoc);
    }

    private OnDocumentDateChange(request: LocalEvent, store: DocumentsStore): void {
        var newDoc = store.DocumentsModel.get("SelectedDocument");
        var mDoc = newDoc.get("MedicalDocumentInfo");
        mDoc.Model.ReceiptDate = request.Get<Date>("value");
        newDoc = newDoc.delete("MedicalDocumentInfo");
        newDoc = newDoc.set("MedicalDocumentInfo", mDoc);
        store.DocumentsModel = store.DocumentsModel.set("SelectedDocument", newDoc);
    }

    private OnTodayClick(request: LocalEvent, store: DocumentsStore): void {
        var newDoc = store.DocumentsModel.get("SelectedDocument");
        var mDoc = newDoc.get("MedicalDocumentInfo");
        mDoc.Model.ReceiptDate = new Date();
        newDoc = newDoc.delete("MedicalDocumentInfo");
        newDoc = newDoc.set("MedicalDocumentInfo", mDoc);
        store.DocumentsModel = store.DocumentsModel.set("SelectedDocument", newDoc);
    }

    private OnHRMStatusChange(request: LocalEvent, store: DocumentsStore): void {
        var newDoc = store.DocumentsModel.get("SelectedDocument");
        newDoc = newDoc.set("SelectedHRMStatus", request.Get<string>("value"));
        store.DocumentsModel = store.DocumentsModel.set("SelectedDocument", newDoc);
    }

    private OnNoteChange(request: LocalEvent, store: DocumentsStore): void {
        var newDoc = store.DocumentsModel.get("SelectedDocument");
        newDoc = newDoc.set("Note", request.Get<string>("value"));
        store.DocumentsModel = store.DocumentsModel.set("SelectedDocument", newDoc);
    }

    private ObservationDateChange(request: LocalEvent, store: DocumentsStore): void {
        var newDoc = store.DocumentsModel.get("SelectedDocument");
        newDoc = newDoc.set("ObservationDate", request.Get<Date>("value"));
        store.DocumentsModel = store.DocumentsModel.set("SelectedDocument", newDoc);
    }

    private AuthoringDateChange(request: LocalEvent, store: DocumentsStore): void {
        var newDoc = store.DocumentsModel.get("SelectedDocument");
        newDoc = newDoc.set("AuthoringDate", request.Get<Date>("value"));
        store.DocumentsModel = store.DocumentsModel.set("SelectedDocument", newDoc);
    }

    private AccompanyingSubClassChange(request: LocalEvent, store: DocumentsStore): void {
        var newDoc = store.DocumentsModel.get("SelectedDocument");
        newDoc = newDoc.set("AccompanyingSubClass", request.Get<string>("value"));
        store.DocumentsModel = store.DocumentsModel.set("SelectedDocument", newDoc);
    }

    private MnemonicChange(request: LocalEvent, store: DocumentsStore): void {
        var newDoc = store.DocumentsModel.get("SelectedDocument");
        newDoc = newDoc.set("AccompanyingMnemonic", request.Get<string>("value"));
        store.DocumentsModel = store.DocumentsModel.set("SelectedDocument", newDoc);
    }

    private SourceAuthorNameChange(request: LocalEvent, store: DocumentsStore): void {
        var newDoc = store.DocumentsModel.get("SelectedDocument");
        newDoc = newDoc.set("SourceAuthorName", request.Get<string>("value"));
        store.DocumentsModel = store.DocumentsModel.set("SelectedDocument", newDoc);
    }

    private SenderFacilityIdChange(request: LocalEvent, store: DocumentsStore): void {
        var newDoc = store.DocumentsModel.get("SelectedDocument");
        newDoc = newDoc.set("SendingFacilityId", request.Get<string>("value"));
        store.DocumentsModel = store.DocumentsModel.set("SelectedDocument", newDoc);
    }

    private SenderFacilityReportChange(request: LocalEvent, store: DocumentsStore): void {
        var newDoc = store.DocumentsModel.get("SelectedDocument");
        newDoc = newDoc.set("SendingFacilityReport", request.Get<string>("value"));
        store.DocumentsModel = store.DocumentsModel.set("SelectedDocument", newDoc);
    }

    public SaveApprovedDocument(request: LocalEvent, store: DocumentsStore): void {
        store.DocumentActions.SaveApprovedDocument(store.DocumentsModel.get("SelectedDocument"));
    }

    private onRemoveSelectedClinician(request: LocalEvent, store: DocumentsStore): void {
        let clinicianToRemove = request.Get<PersonDropdownItem>("value");

        if (clinicianToRemove == null || clinicianToRemove.Item == null)
            return;

        let newDoc = store.DocumentsModel.get("SelectedDocument");
        let filteredClinicians = newDoc.get("SelectedClinicians").filterImmutable(x => x.ContactId != clinicianToRemove.Item.ContactId);

        newDoc = newDoc.set("SelectedClinicians", filteredClinicians);
        store.DocumentsModel = store.DocumentsModel.set("SelectedDocument", newDoc);
    }

    private loadAttachedTasks(request: LocalEvent, store: DocumentsStore): void {
        let documentId = request.Get<string>("documentId");
        store.DocumentActions.loadAttachedTasks(documentId);
    }

    private UpdateInboundFaxData(request: LocalEvent, store: DocumentsStore): void {
        const stateChanges = request.Get<Partial<InboundFaxMailboxModel>>("stateChanges");
        const uiStateOverride = request.Get<DocumentUIState>("uiStateOverride");

        const currentUIState = uiStateOverride ?? store.DocumentsModel.get("UIState");

        let currentState = null;
        switch (currentUIState) {
            case DocumentUIState.InboundFaxInboxMailbox:
                currentState = store.InboundFaxInboxModel;
                store.InboundFaxInboxModel = { ...currentState, ...stateChanges };
                break;
            case DocumentUIState.InboundFaxTrashMailbox:
                currentState = store.InboundFaxTrashModel;
                store.InboundFaxTrashModel = { ...currentState, ...stateChanges };
                break;
            case DocumentUIState.InboundFaxIndexedMailbox:
                currentState = store.InboundFaxIndexedModel;
                store.InboundFaxIndexedModel = { ...currentState, ...stateChanges };
                break;
        }
    }
}

export { UpdateDocumentHandler };
